import {
  AdditiveBlending,
  Color,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { Layer } from './Layer.js';
import { createBowGeometry, createViolinGeometry } from '../geometry/violin.js';
import { VIOLIN_VOICES, hzFor } from '../audio/scales.js';

/**
 * "…and violins are made."
 *
 * Seven instruments: most left on the bench course along the wall, two turning
 * slowly over the well because this is a dreamed city and dreamed cities do
 * that. Each holds one note of a D-minor eleventh, and bows itself as the
 * traveller comes near — so walking the stair plays the chord, and stopping
 * holds it.
 *
 * Nothing is clicked. The only instrument is distance.
 */
export class Violins extends Layer {
  static id = 'violins';
  static order = 55;

  async build() {
    const { settings, palette, helix, bus } = this.ctx;
    const cfg = settings.violins;
    const s = settings.stair;

    const parts = createViolinGeometry();
    const bow = createBowGeometry();

    this.varnishMat = new MeshStandardMaterial({
      color: palette.get('varnish').clone(),
      roughness: 0.22,
      metalness: 0.08,
      emissive: new Color(0x000000),
    });
    this.ebonyMat = new MeshStandardMaterial({
      color: new Color(0x16120f),
      roughness: 0.4,
      metalness: 0.05,
    });
    this.stringMat = new MeshStandardMaterial({
      color: palette.get('rosin').clone(),
      roughness: 0.3,
      metalness: 0.75,
      emissive: new Color(0x000000),
    });
    this.hairMat = new MeshStandardMaterial({
      color: new Color(0xe8e0cd),
      roughness: 0.85,
      metalness: 0.0,
    });

    this.items = [];
    // The model is 1.0 long in the body and about 1.66 overall, so this puts
    // the instrument at the 59.5 cm a real violin measures scroll to button.
    const SCALE = 0.36;

    for (let i = 0; i < cfg.count; i++) {
      const u = cfg.count === 1 ? 0.5 : i / (cfg.count - 1);
      const t = cfg.firstAt + u * (cfg.lastAt - cfg.firstAt);
      const a = helix.angleAt(t);
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      const y = helix.heightAt(t);
      const floating = i % 3 === 1;

      const g = new Group();
      const radius = floating ? s.innerRadius - 1.9 : s.outerRadius - 1.0;
      const baseY = floating ? y + 1.5 : y + 0.52;
      g.position.set(cos * radius, baseY, sin * radius);
      g.scale.setScalar(SCALE);

      const body = new Mesh(parts.varnish, this.varnishMat);
      const ebony = new Mesh(parts.ebony, this.ebonyMat);
      const strings = new Mesh(parts.strings, this.stringMat);
      g.add(body, ebony, strings);

      // The bow, resting across the strings, waiting.
      const bowGroup = new Group();
      bowGroup.add(new Mesh(bow.wood, this.ebonyMat), new Mesh(bow.hair, this.hairMat));
      bowGroup.rotation.z = Math.PI / 2;
      bowGroup.position.set(0, -0.055, 0.17);
      g.add(bowGroup);

      if (floating) {
        g.rotation.set(0.25, -a + Math.PI * 0.4, 0.1);
      } else {
        // Leaning against the wall, face out, the way they are left.
        g.rotation.set(-Math.PI * 0.44, -a + Math.PI / 2, Math.PI * 0.07);
      }

      // A rosin-coloured glimmer so a violin can be found in the dark. It
      // must stay smaller than the instrument, or it reads as a bauble.
      const spark = new Mesh(
        new SphereGeometry(0.075, 12, 10),
        new MeshBasicMaterial({
          color: palette.get('rosin').clone(),
          transparent: true,
          opacity: 0.1,
          blending: AdditiveBlending,
          depthWrite: false,
        }),
      );
      // Behind the plate, so it haloes the instrument instead of hiding it.
      spark.position.set(0, -0.12, -0.14);
      g.add(spark);

      this.group.add(g);
      this.items.push({
        index: i,
        t,
        group: g,
        bow: bowGroup,
        spark,
        floating,
        baseY,
        baseRotation: g.rotation.clone(),
        note: VIOLIN_VOICES[i % VIOLIN_VOICES.length],
        hz: hzFor(VIOLIN_VOICES[i % VIOLIN_VOICES.length]),
        bowAmount: 0,
        warmth: 0,
        remembered: false,
      });
    }

    this.ctx.gaze.register({
      kind: 'violin',
      mode: 'mesh',
      maxDistance: 22,
      objects: () => this.items.map((it) => it.group),
      owner: (obj) => {
        let o = obj;
        while (o && !this.items.some((it) => it.group === o)) o = o.parent;
        return o;
      },
      resolve: (owner) => ({ item: this.items.find((it) => it.group === owner) }),
    });

    bus.on('remember', (e) => {
      if (e.kind !== 'violin') return;
      const item = e.data?.item;
      if (!item || item.remembered) return;
      item.remembered = true;
      this.ctx.audio?.violin(item.index, item.hz)?.swell();
    });

    this._world = new Vector3();
    this._right = new Vector3();
  }

  update(dt, state) {
    const { palette, gaze, camera, audio, settings } = this.ctx;
    this.varnishMat.color.copy(palette.get('varnish'));
    this.stringMat.color.copy(palette.get('rosin'));

    const gazed = gaze.current?.kind === 'violin' ? gaze.current.data?.item : null;
    const radius = settings.audio.violinRadius;

    // The listener's right ear, for panning.
    camera.getWorldDirection(this._world);
    this._right.set(-this._world.z, 0, this._world.x).normalize();

    let hottest = 0;
    for (const it of this.items) {
      const d = it.group.position.distanceTo(camera.position);
      const near = Math.max(0, 1 - d / radius);
      let bow = Math.pow(near, 1.7);
      if (it === gazed) bow = Math.min(1, bow + 0.35 + gaze.progress * 0.5);
      if (it.remembered) bow *= 0.55; // a remembered violin plays more quietly
      it.bowAmount += (bow - it.bowAmount) * Math.min(1, dt * 3.2);

      const want = Math.max(it.bowAmount * 0.7, it === gazed ? 0.4 + gaze.progress * 0.6 : 0);
      it.warmth += (want - it.warmth) * Math.min(1, dt * 5);
      hottest = Math.max(hottest, it.warmth);

      // Draw the bow: a slow down-bow and up-bow, deeper when pressed harder.
      const speed = 0.35 + it.bowAmount * 0.75;
      const stroke = Math.sin(state.elapsed * speed + it.index * 2.1);
      it.bow.position.y = -0.055 + stroke * 0.3 * it.bowAmount;
      it.bow.position.z = 0.17 - it.bowAmount * 0.055;
      it.bow.rotation.x = stroke * 0.05 * it.bowAmount;

      // Floating violins turn; resting ones only breathe.
      if (it.floating) {
        it.group.rotation.y = it.baseRotation.y + state.elapsed * 0.075;
        it.group.rotation.z = it.baseRotation.z + Math.sin(state.elapsed * 0.4 + it.index) * 0.06;
        it.group.position.y = it.baseY + Math.sin(state.elapsed * 0.33 + it.index * 2) * 0.16;
      } else {
        it.group.position.y = it.baseY + Math.sin(state.elapsed * 0.6 + it.index) * 0.004;
      }

      it.spark.material.opacity = 0.05 + it.warmth * 0.22;
      it.spark.scale.setScalar(1 + it.warmth * 0.5);

      // Sound.
      if (audio?.ready && it.bowAmount > 0.004) {
        const voice = audio.violin(it.index, it.hz);
        const toward = this._world.set(
          it.group.position.x - camera.position.x,
          0,
          it.group.position.z - camera.position.z,
        );
        const pan = Math.max(-1, Math.min(1, toward.normalize().dot(this._right)));
        voice?.set(it.bowAmount, pan, it === gazed ? gaze.progress * 0.6 : 0);
      } else if (audio?.ready) {
        audio.violins.get(it.index)?.set(0, 0, 0);
      }
    }

    this.varnishMat.emissive.copy(palette.get('rosin')).multiplyScalar(hottest * 0.2);
    this.stringMat.emissive.copy(palette.get('desire')).multiplyScalar(hottest * 0.45);
  }

  stats() {
    return {
      violins: this.items.length,
      bowing: this.items.filter((i) => i.bowAmount > 0.05).length,
    };
  }
}
